// Flat-image thumbnail bakers used at submission time. The customer's
// builders (Designer + DTF) call these once per piece of submitted media
// to render a single PNG, upload it to /api/files, and write the resulting
// file id into spec.pieceThumbnails. Admin pages then <img src> the baked
// PNG instead of re-running Fabric.js for every thumbnail render.
//
// We keep everything browser-side so we don't have to install node-canvas
// in the API container; the customer's browser already has Fabric loaded.
import { fabric } from 'fabric'

// Must match Designer.tsx / OrderDetail.tsx so the spec coords land at
// the same pixel positions on the baked image as they do on the live
// canvas the customer was looking at.
const GARMENT_W = 640
const GARMENT_H = 720
const DTF_W = 570

// ── Public API ───────────────────────────────────────────────────────────

// Bake one garment-side thumbnail (mockup background + fabric overlay).
// Returns null if there's nothing to render (no canvas + no mockup).
export async function bakeGarmentThumbnail(
  mockupUrl: string | null,
  canvasJSON: object | null,
  apiSrc: (path: string | null) => string,
  outWidth = 800,
): Promise<string | null> {
  if (!mockupUrl && !canvasJSON) return null

  const W = GARMENT_W
  const H = GARMENT_H
  const outHeight = Math.round(outWidth * H / W)

  // Output canvas — composite target.
  const out = document.createElement('canvas')
  out.width = outWidth
  out.height = outHeight
  const ctx = out.getContext('2d')
  if (!ctx) return null

  // Always start with white so transparent mockups don't bleed checkered
  // into the rendered PNG.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, outWidth, outHeight)

  // 1) Mockup img — letterboxed object-contain-style, matching the live
  //    Designer's `w-full h-full object-contain` layout.
  if (mockupUrl) {
    try {
      const img = await loadImage(apiSrc(mockupUrl))
      const scale = Math.min(outWidth / img.width, outHeight / img.height)
      const w = img.width * scale
      const h = img.height * scale
      const x = (outWidth - w) / 2
      const y = (outHeight - h) / 2
      ctx.drawImage(img, x, y, w, h)
    } catch {
      // Mockup load failed — bake without it. The fabric overlay alone
      // is still useful for the admin to see what's been submitted.
    }
  }

  // 2) Fabric canvas — render the saved JSON to an offscreen Fabric
  //    StaticCanvas, then draw the result over the mockup at the same
  //    640x720 -> outWidth x outHeight transform.
  if (canvasJSON) {
    const tmpEl = document.createElement('canvas')
    tmpEl.width = W
    tmpEl.height = H
    const tmpFabric = new fabric.StaticCanvas(tmpEl, {
      width: W, height: H, backgroundColor: 'transparent',
      enableRetinaScaling: false, renderOnAddRemove: false,
    })
    try {
      await loadFabricFromJSON(tmpFabric, canvasJSON)
      tmpFabric.renderAll()
      ctx.drawImage(tmpEl, 0, 0, outWidth, outHeight)
    } catch {
      // Fabric load failed — keep just the mockup.
    } finally {
      tmpFabric.dispose()
    }
  }

  return out.toDataURL('image/png')
}

// Bake one DTF gang-sheet thumbnail (white sheet + fabric overlay). The
// sheet's natural width is fixed at 57 cm; the page-supplied lengthCm
// (default 100) determines aspect ratio.
export async function bakeDtfSheetThumbnail(
  canvasJSON: object | null,
  lengthCm: number | null,
  outWidth = 720,
): Promise<string | null> {
  if (!canvasJSON) return null

  const W = DTF_W
  const H = (lengthCm ?? 100) * 10
  const outHeight = Math.round(outWidth * H / W)

  const out = document.createElement('canvas')
  out.width = outWidth
  out.height = outHeight
  const ctx = out.getContext('2d')
  if (!ctx) return null

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, outWidth, outHeight)

  const tmpEl = document.createElement('canvas')
  tmpEl.width = W
  tmpEl.height = H
  const tmpFabric = new fabric.StaticCanvas(tmpEl, {
    width: W, height: H, backgroundColor: '#ffffff',
    enableRetinaScaling: false, renderOnAddRemove: false,
  })
  try {
    await loadFabricFromJSON(tmpFabric, canvasJSON)
    tmpFabric.renderAll()
    ctx.drawImage(tmpEl, 0, 0, outWidth, outHeight)
  } catch {
    // Keep the blank white sheet rather than throwing.
  } finally {
    tmpFabric.dispose()
  }

  return out.toDataURL('image/png')
}

// Upload a baked thumbnail PNG to /api/files and return the file id. Use
// kind='mockup' so the existing file storage / cleanup heuristics treat
// it as a catalog-side asset (it's referenced by artwork_approvals, not
// part of a customer artwork bundle).
export async function uploadBakedThumbnail(
  dataUrl: string,
  originalName: string,
): Promise<string> {
  const resp = await fetch('/api/files', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      originalName,
      mimeType: 'image/png',
      base64: dataUrl,
      kind: 'mockup',
    }),
  })
  if (!resp.ok) {
    const t = await resp.text().catch(() => '')
    throw new Error(`Thumbnail upload failed (${resp.status}): ${t}`)
  }
  const j = (await resp.json()) as { id: string }
  return j.id
}

// ── Internals ───────────────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // Same-origin in production (apiSrc returns same-host path); set
    // crossOrigin so the canvas isn't tainted if the mockup is ever
    // served from a different host in development.
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(e)
    img.src = src
  })
}

function loadFabricFromJSON(c: fabric.StaticCanvas, json: object): Promise<void> {
  return new Promise((resolve) => {
    // Fabric's loadFromJSON callback fires after embedded images have
    // loaded, so this awaits async image decode for us.
    c.loadFromJSON(json, () => resolve())
  })
}
